/**
 * Resolve the authoring-time output schema / field list for a workflow
 * node. Used by Connect-mode mapping editors and llm-agent token
 * coverage so they agree on what each node emits.
 *
 * Resolution order for field names (`getEffectiveOutputFieldNames`):
 *   - per-node outputMapping keys when the mapping parses as a simple
 *     Connect-mode object (matches what the runner stores after rename)
 *   - else schema properties from the resolution order below
 *
 * Schema resolution (`resolveEffectiveOutputSchema`):
 *   - `llm-agent` → its inline `config.outputSchema` (DEV-16943: the node is
 *     self-contained; no Agent row is referenced)
 *   - persisted node.data.outputSchema (when non-empty)
 *   - node-type registry default (when persisted is empty)
 */

import { topLevelKeys } from '../MappingRow';

import { parseJsonataObject } from './jsonata-object-mapping';
import { isEmptyOutputSchema, normalizeRegistryOutputSchema } from './node-type-schemas';

export type WorkflowNodeSchemaData = {
  nodeType?: string;
  outputSchema?: Record<string, unknown>;
  config?: { outputSchema?: unknown };
};

export type WorkflowNodeWireData = WorkflowNodeSchemaData & {
  outputMapping?: string | null;
};

export type OutputSchemaContext = {
  nodeTypeOutputSchemaByKey?: ReadonlyMap<string, Record<string, unknown> | null> | null;
};

/** Resolve effective output schema from node data (may be null / empty). */
export function resolveEffectiveOutputSchema(
  data: WorkflowNodeSchemaData,
  nodeTypeOutputSchemaByKey?: ReadonlyMap<string, Record<string, unknown> | null> | null,
): Record<string, unknown> | null {
  if (data.nodeType === 'llm-agent') {
    // Structured-output schema lives inline on the node config now. Empty /
    // absent → the node emits an unstructured payload (null coverage).
    const inline = data.config?.outputSchema;
    if (!inline || typeof inline !== 'object' || Array.isArray(inline)) return null;
    const schema = inline as Record<string, unknown>;
    if (isEmptyOutputSchema(schema)) return null;
    return normalizeRegistryOutputSchema(schema) ?? schema;
  }
  if (!isEmptyOutputSchema(data.outputSchema)) {
    return normalizeRegistryOutputSchema(data.outputSchema) ?? data.outputSchema ?? null;
  }
  const nodeType = data.nodeType ?? '';
  if (nodeType && nodeTypeOutputSchemaByKey) {
    const def = normalizeRegistryOutputSchema(nodeTypeOutputSchemaByKey.get(nodeType) ?? null);
    if (!isEmptyOutputSchema(def)) return def;
  }
  return data.outputSchema ?? null;
}

/** Top-level keys from a Connect-mode per-node outputMapping, if parseable. */
function publishedFieldNamesFromOutputMapping(
  outputMapping: string | null | undefined,
): string[] | null {
  if (!outputMapping) return null;
  const parsed = parseJsonataObject(outputMapping);
  if (!parsed || Object.keys(parsed).length === 0) return null;
  return Object.keys(parsed);
}

/**
 * Top-level field names this node publishes to downstream nodes — after
 * optional output renamings. Matches runtime `previousOutputs[nodeKey]`.
 */
export function getEffectiveOutputFieldNames(
  data: WorkflowNodeWireData,
  ctx: OutputSchemaContext,
): string[] {
  if (data.nodeType !== 'llm-agent') {
    const fromMapping = publishedFieldNamesFromOutputMapping(data.outputMapping);
    if (fromMapping) return fromMapping;
  }

  const schema = resolveEffectiveOutputSchema(data, ctx.nodeTypeOutputSchemaByKey);
  return topLevelKeys(schema);
}

/** Look up a node by key and resolve its effective output schema. */
export function getEffectiveOutputSchemaByNodeKey(
  getNode: (key: string) => { data?: unknown } | null | undefined,
  nodeKey: string,
  ctx: OutputSchemaContext,
): Record<string, unknown> | null {
  const node = getNode(nodeKey);
  if (!node) return null;
  return resolveEffectiveOutputSchema(
    (node.data ?? {}) as WorkflowNodeSchemaData,
    ctx.nodeTypeOutputSchemaByKey,
  );
}

/**
 * Same as {@link getEffectiveOutputSchemaByNodeKey} but returns `null`
 * when the schema is missing or `{}` — matches token-coverage semantics.
 */
export function getDeclaredOutputSchemaByNodeKey(
  getNode: (key: string) => { data?: unknown } | null | undefined,
  nodeKey: string,
  ctx: OutputSchemaContext,
): Record<string, unknown> | null {
  const schema = getEffectiveOutputSchemaByNodeKey(getNode, nodeKey, ctx);
  return schema && Object.keys(schema).length > 0 ? schema : null;
}

/** Field names for a node key — used by Connect dropdowns and token chips. */
export function getEffectiveOutputFieldNamesByNodeKey(
  getNode: (key: string) => { data?: unknown } | null | undefined,
  nodeKey: string,
  ctx: OutputSchemaContext,
): string[] {
  const node = getNode(nodeKey);
  if (!node) return [];
  return getEffectiveOutputFieldNames((node.data ?? {}) as WorkflowNodeWireData, ctx);
}
